import { supabase } from '../utils/supabase';

class UserService {
  // Get user profile by ID
  async getUserProfile(userId) {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select(`
          *,
          wedding_profiles (*),
          user_preferences (*)
        `)
        .eq('id', userId)
        .single();

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      if (error?.message?.includes('Failed to fetch')) {
        return { success: false, error: 'Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.' };
      }
      return { success: false, error: 'Failed to load user profile' };
    }
  }

  // Update user profile
  async updateUserProfile(userId, updates) {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', userId)
        .select()
        .single();

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      if (error?.message?.includes('Failed to fetch')) {
        return { success: false, error: 'Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.' };
      }
      return { success: false, error: 'Failed to update user profile' };
    }
  }

  // Create or update wedding profile
  async createOrUpdateWeddingProfile(userId, weddingData) {
    try {
      const { data, error } = await supabase
        .from('wedding_profiles')
        .upsert({
          user_id: userId,
          ...weddingData,
          updated_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      if (error?.message?.includes('Failed to fetch')) {
        return { success: false, error: 'Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.' };
      }
      return { success: false, error: 'Failed to save wedding profile' };
    }
  }

  // Get wedding profile
  async getWeddingProfile(userId) {
    try {
      const { data, error } = await supabase
        .from('wedding_profiles')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
        return { success: false, error: error.message };
      }

      return { success: true, data: data || null };
    } catch (error) {
      if (error?.message?.includes('Failed to fetch')) {
        return { success: false, error: 'Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.' };
      }
      return { success: false, error: 'Failed to load wedding profile' };
    }
  }

  // Create or update user preferences
  async createOrUpdateUserPreferences(userId, preferences) {
    try {
      const { data, error } = await supabase
        .from('user_preferences')
        .upsert({
          user_id: userId,
          ...preferences,
          updated_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      if (error?.message?.includes('Failed to fetch')) {
        return { success: false, error: 'Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.' };
      }
      return { success: false, error: 'Failed to save preferences' };
    }
  }

  // Get user preferences
  async getUserPreferences(userId) {
    try {
      const { data, error } = await supabase
        .from('user_preferences')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (error && error.code !== 'PGRST116') {
        return { success: false, error: error.message };
      }

      return { success: true, data: data || null };
    } catch (error) {
      if (error?.message?.includes('Failed to fetch')) {
        return { success: false, error: 'Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.' };
      }
      return { success: false, error: 'Failed to load preferences' };
    }
  }

  // Get saved vendors for a user
  async getSavedVendors(userId) {
    try {
      const { data, error } = await supabase
        .from('saved_vendors')
        .select(`
          *,
          vendor_profiles:vendor_id (
            id,
            business_name,
            category,
            city,
            state,
            starting_price,
            is_verified,
            vendor_gallery (
              image_url,
              is_featured
            )
          )
        `)
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data: data || [] };
    } catch (error) {
      if (error?.message?.includes('Failed to fetch')) {
        return { success: false, error: 'Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.' };
      }
      return { success: false, error: 'Failed to load saved vendors' };
    }
  }

  // Save vendor for user
  async saveVendor(userId, vendorId) {
    try {
      const { data, error } = await supabase
        .from('saved_vendors')
        .insert({
          user_id: userId,
          vendor_id: vendorId
        })
        .select()
        .single();

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      if (error?.message?.includes('Failed to fetch')) {
        return { success: false, error: 'Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.' };
      }
      return { success: false, error: 'Failed to save vendor' };
    }
  }

  // Remove saved vendor
  async removeSavedVendor(userId, vendorId) {
    try {
      const { error } = await supabase
        .from('saved_vendors')
        .delete()
        .eq('user_id', userId)
        .eq('vendor_id', vendorId);

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      if (error?.message?.includes('Failed to fetch')) {
        return { success: false, error: 'Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.' };
      }
      return { success: false, error: 'Failed to remove saved vendor' };
    }
  }

  // Get user's inquiries
  async getUserInquiries(userId) {
    try {
      const { data, error } = await supabase
        .from('inquiries')
        .select(`
          *,
          vendor_profiles:vendor_id (
            id,
            business_name,
            category,
            city,
            state,
            phone,
            email,
            vendor_gallery (
              image_url,
              is_featured
            )
          )
        `)
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data: data || [] };
    } catch (error) {
      if (error?.message?.includes('Failed to fetch')) {
        return { success: false, error: 'Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.' };
      }
      return { success: false, error: 'Failed to load inquiries' };
    }
  }

  // Get user's bookings
  async getUserBookings(userId) {
    try {
      const { data, error } = await supabase
        .from('bookings')
        .select(`
          *,
          vendor_profiles:vendor_id (
            id,
            business_name,
            category,
            city,
            state,
            phone,
            email,
            vendor_gallery (
              image_url,
              is_featured
            )
          ),
          vendor_packages:package_id (
            id,
            name,
            description,
            price,
            features
          )
        `)
        .eq('user_id', userId)
        .order('event_date', { ascending: true });

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data: data || [] };
    } catch (error) {
      if (error?.message?.includes('Failed to fetch')) {
        return { success: false, error: 'Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.' };
      }
      return { success: false, error: 'Failed to load bookings' };
    }
  }

  // Create inquiry
  async createInquiry(userId, inquiryData) {
    try {
      const { data, error } = await supabase
        .from('inquiries')
        .insert({
          user_id: userId,
          ...inquiryData
        })
        .select()
        .single();

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      if (error?.message?.includes('Failed to fetch')) {
        return { success: false, error: 'Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.' };
      }
      return { success: false, error: 'Failed to create inquiry' };
    }
  }

  // Create booking
  async createBooking(userId, bookingData) {
    try {
      const { data, error } = await supabase
        .from('bookings')
        .insert({
          user_id: userId,
          ...bookingData
        })
        .select()
        .single();

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      if (error?.message?.includes('Failed to fetch')) {
        return { success: false, error: 'Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.' };
      }
      return { success: false, error: 'Failed to create booking' };
    }
  }

  // Check if vendor is saved by user
  async isVendorSaved(userId, vendorId) {
    try {
      const { data, error } = await supabase
        .from('saved_vendors')
        .select('id')
        .eq('user_id', userId)
        .eq('vendor_id', vendorId)
        .single();

      if (error && error.code !== 'PGRST116') {
        return { success: false, error: error.message };
      }

      return { success: true, data: !!data };
    } catch (error) {
      if (error?.message?.includes('Failed to fetch')) {
        return { success: false, error: 'Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.' };
      }
      return { success: false, error: 'Failed to check saved status' };
    }
  }

  // Subscribe to real-time updates for user data
  subscribeToUserUpdates(userId, callback) {
    const subscription = supabase
      .channel(`user_updates_${userId}`)
      .on(
        'postgres_changes',
        { 
          event: '*', 
          schema: 'public', 
          table: 'user_profiles',
          filter: `id=eq.${userId}`
        },
        callback
      )
      .on(
        'postgres_changes',
        { 
          event: '*', 
          schema: 'public', 
          table: 'wedding_profiles',
          filter: `user_id=eq.${userId}`
        },
        callback
      )
      .on(
        'postgres_changes',
        { 
          event: '*', 
          schema: 'public', 
          table: 'saved_vendors',
          filter: `user_id=eq.${userId}`
        },
        callback
      )
      .subscribe();

    return subscription;
  }

  // Unsubscribe from real-time updates
  unsubscribeFromUpdates(subscription) {
    if (subscription) {
      supabase.removeChannel(subscription);
    }
  }
}

// Export singleton instance
const userService = new UserService();
export default userService;